import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft,
  Building2,
  CheckCircle,
  XCircle,
  Star,
  MapPin,
  Phone,
  Mail,
  Clock,
  Filter,
  Search,
  Loader2,
  Shield,
  Award,
  Trash2,
  Eye,
  Users,
  TrendingUp
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { getAllStates } from '@/data/stateData';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const industryNames = {
  hvac: "HVAC & Heating",
  plumbing: "Plumbing",
  electrical: "Electrical",
  home_repair: "Home Repair",
  roofing: "Roofing",
  landscaping: "Landscaping",
  auto_repair: "Auto Repair",
  septic: "Septic Services",
  dental: "Dental & Medical",
  restaurant: "Restaurant",
  fishing_charter: "Fishing Charter",
  sightseeing: "Sightseeing Tours",
  gym: "Gym & Fitness"
};

export default function AdminContractors() {
  const navigate = useNavigate();
  const states = getAllStates();
  
  const [contractors, setContractors] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);
  
  // Filters
  const [filterState, setFilterState] = useState('');
  const [filterIndustry, setFilterIndustry] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchContractors();
    fetchStats();
  }, [filterState, filterIndustry, filterStatus]);

  const fetchContractors = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filterState) params.append('state', filterState);
      if (filterIndustry) params.append('industry', filterIndustry);
      if (filterStatus) params.append('status', filterStatus);
      
      const response = await axios.get(`${BACKEND_URL}/api/contractors/admin/all?${params}`);
      setContractors(response.data.contractors || []);
    } catch (error) {
      console.error('Error fetching contractors:', error);
      toast.error('Failed to load contractors');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/contractors/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const updateContractor = async (contractorId, updates) => {
    setUpdating(contractorId);
    try {
      await axios.put(`${BACKEND_URL}/api/contractors/admin/${contractorId}`, updates);
      toast.success('Contractor updated');
      fetchContractors();
      fetchStats();
    } catch (error) {
      console.error('Update error:', error);
      toast.error('Failed to update contractor');
    } finally {
      setUpdating(null);
    }
  };

  const deleteContractor = async (contractorId, businessName) => {
    if (!window.confirm(`Are you sure you want to delete "${businessName}"? This cannot be undone.`)) {
      return;
    }
    
    setUpdating(contractorId);
    try {
      await axios.delete(`${BACKEND_URL}/api/contractors/admin/${contractorId}`);
      toast.success('Contractor deleted');
      fetchContractors();
      fetchStats();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete contractor');
    } finally {
      setUpdating(null);
    }
  };

  const filteredContractors = contractors.filter(c => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      c.business_name?.toLowerCase().includes(query) ||
      c.email?.toLowerCase().includes(query) ||
      c.city?.toLowerCase().includes(query)
    );
  });

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <header className="border-b border-slate-800 bg-slate-900/50 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate('/admin/clients')}
                className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-400 to-pink-600 flex items-center justify-center">
                  <Building2 className="w-4 h-4 text-white" />
                </div>
                <span className="font-bold text-lg">Contractor Management</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-slate-900 border border-slate-800 rounded-xl p-4"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <div className="text-2xl font-bold">{stats.total}</div>
                  <div className="text-sm text-slate-400">Total</div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-slate-900 border border-slate-800 rounded-xl p-4"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-yellow-400" />
                </div>
                <div>
                  <div className="text-2xl font-bold">{stats.pending}</div>
                  <div className="text-sm text-slate-400">Pending</div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-slate-900 border border-slate-800 rounded-xl p-4"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <div className="text-2xl font-bold">{stats.verified}</div>
                  <div className="text-sm text-slate-400">Verified</div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-slate-900 border border-slate-800 rounded-xl p-4"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                  <Award className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <div className="text-2xl font-bold">{stats.featured}</div>
                  <div className="text-sm text-slate-400">Featured</div>
                </div>
              </div>
            </motion.div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4 mb-6">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by name, email, city..."
                  className="pl-10 bg-slate-800 border-slate-700"
                />
              </div>
            </div>
            
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="h-10 px-3 rounded-md bg-slate-800 border border-slate-700 text-white"
            >
              <option value="">All Status</option>
              <option value="pending">Pending Verification</option>
              <option value="verified">Verified</option>
              <option value="featured">Featured</option>
            </select>
            
            <select
              value={filterState}
              onChange={(e) => setFilterState(e.target.value)}
              className="h-10 px-3 rounded-md bg-slate-800 border border-slate-700 text-white"
            >
              <option value="">All States</option>
              {states.map(state => (
                <option key={state.abbr} value={state.abbr}>{state.name}</option>
              ))}
            </select>
            
            <select
              value={filterIndustry}
              onChange={(e) => setFilterIndustry(e.target.value)}
              className="h-10 px-3 rounded-md bg-slate-800 border border-slate-700 text-white"
            >
              <option value="">All Industries</option>
              {Object.entries(industryNames).map(([id, name]) => (
                <option key={id} value={id}>{name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Contractors Table */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-sky-400" />
          </div>
        ) : filteredContractors.length === 0 ? (
          <div className="text-center py-20">
            <Building2 className="w-16 h-16 text-slate-700 mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">No Contractors Found</h3>
            <p className="text-slate-400">Try adjusting your filters</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredContractors.map((contractor, index) => (
              <motion.div
                key={contractor.contractor_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.03 }}
                className="bg-slate-900 border border-slate-800 rounded-xl p-4 hover:border-slate-700 transition-colors"
              >
                <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                  {/* Business Info */}
                  <div className="flex-1">
                    <div className="flex items-start gap-3">
                      <div className="w-12 h-12 rounded-lg bg-slate-800 flex items-center justify-center text-xl font-bold">
                        {contractor.business_name?.charAt(0) || '?'}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-bold text-lg">{contractor.business_name}</h3>
                          {contractor.is_verified && (
                            <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full flex items-center gap-1">
                              <CheckCircle className="w-3 h-3" /> Verified
                            </span>
                          )}
                          {contractor.is_featured && (
                            <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 text-xs rounded-full flex items-center gap-1">
                              <Award className="w-3 h-3" /> Featured
                            </span>
                          )}
                          {!contractor.is_verified && (
                            <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-xs rounded-full">
                              Pending
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-slate-400">
                          {industryNames[contractor.industry] || contractor.industry}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex flex-wrap gap-4 mt-3 text-sm text-slate-400">
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {contractor.city}, {contractor.state}
                      </span>
                      <span className="flex items-center gap-1">
                        <Phone className="w-4 h-4" />
                        {contractor.phone}
                      </span>
                      <span className="flex items-center gap-1">
                        <Mail className="w-4 h-4" />
                        {contractor.email}
                      </span>
                      {contractor.rating && (
                        <span className="flex items-center gap-1 text-yellow-400">
                          <Star className="w-4 h-4 fill-current" />
                          {contractor.rating} ({contractor.review_count} reviews)
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex flex-wrap gap-2">
                    {!contractor.is_verified && (
                      <Button
                        size="sm"
                        onClick={() => updateContractor(contractor.contractor_id, { is_verified: true })}
                        disabled={updating === contractor.contractor_id}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        {updating === contractor.contractor_id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Verify
                          </>
                        )}
                      </Button>
                    )}
                    
                    {contractor.is_verified && !contractor.is_featured && (
                      <Button
                        size="sm"
                        onClick={() => updateContractor(contractor.contractor_id, { is_featured: true })}
                        disabled={updating === contractor.contractor_id}
                        className="bg-purple-600 hover:bg-purple-700"
                      >
                        <Award className="w-4 h-4 mr-1" />
                        Feature
                      </Button>
                    )}
                    
                    {contractor.is_featured && (
                      <Button
                        size="sm"
                        onClick={() => updateContractor(contractor.contractor_id, { is_featured: false })}
                        disabled={updating === contractor.contractor_id}
                        variant="outline"
                        className="border-purple-500 text-purple-400 hover:bg-purple-500/10"
                      >
                        <Award className="w-4 h-4 mr-1" />
                        Unfeature
                      </Button>
                    )}
                    
                    <Button
                      size="sm"
                      onClick={() => navigate(`/contractors/${contractor.state}`)}
                      variant="outline"
                      className="border-slate-600 hover:bg-slate-800"
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      View
                    </Button>
                    
                    <Button
                      size="sm"
                      onClick={() => deleteContractor(contractor.contractor_id, contractor.business_name)}
                      disabled={updating === contractor.contractor_id}
                      variant="outline"
                      className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
